import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { customerService } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { parseApiFormErrors } from '../../../services/apiErrors';
import BoundedPageLayout from '../../../components/dashboard/BoundedPageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';
import LoadingState from '../../../components/dashboard/LoadingState';
import ErrorState from '../../../components/dashboard/ErrorState';
import PermissionDenied from '../../../components/dashboard/PermissionDenied';
import SuccessSnackbar from '../../../components/dashboard/SuccessSnackbar';
import { FlagOption, languages } from '../../../components/LanguageSelector';
import { retryOnServerError } from '../../../components/dashboard/queryOptions';
import { maskedInputSlotProps } from '../../../components/dashboard/MaskedInput';
import {
  CityAutocomplete,
  CountryAutocomplete,
  PostalCodeField,
  StateAutocomplete,
  useAddressLocations,
} from '../../../components/dashboard/LocationFields';
import usePostalCodeLookup from '../../../components/dashboard/usePostalCodeLookup';
import {
  formatPhoneInput,
  nextPhoneInput,
  POSTAL_CODE_MASKS,
  TAX_ID_MASKS,
  toE164,
} from '../../../utils/formatters';
import {
  ADDRESS_FIELDS,
  COMPANY_FIELDS,
  COMPANY_FIELD_HELPER_KEY,
  COMPANY_FIELD_LABEL_KEY,
  normalizeTaxId,
  REQUIRED_ADDRESS_FIELDS,
  REQUIRED_COMPANY_FIELDS,
  COMPANY_FIELD_RULES,
  normalizeCountry,
  validateFieldValue,
} from '../../../components/dashboard/companyFields';
import { CompanyTypeChip } from '../../../components/dashboard/BusinessChips';
import { businessCapabilities, sharedNames } from './columns';

const CUSTOMER_PARAM = 'customer';
const COMPANY_TYPES = ['independent', 'headquarters', 'branch'];

const IDENTIFICATION_FIELDS = ['customer_uuid', 'display_name', 'language'];
// Stores take a `phones` list (several contact numbers, handed out by the AI
// agent) instead of the single `phone_number` of the customer onboarding.
const ADDRESS_ROW_FIELDS = ADDRESS_FIELDS.filter((field) => field !== 'phone_number');
const PHONE_TYPES = ['landline', 'mobile', 'whatsapp'];
const emptyPhone = () => ({ number: '', type: 'landline' });
// Required to create the primary commercial address on edit when the
// company has none yet (e.g. created by the checkout provisioning).
const REQUIRED_NEW_ADDRESS_FIELDS = ['address_street', 'address_city', 'address_state', 'address_country'];
// Locked when the company data is shared: another tax ID/country would be
// another company (see BusinessUpdateSerializer).
const SHARED_LOCKED_FIELDS = ['persona_tax_id', 'persona_country'];

// Keys are the backend's flat field names (BusinessStoreCreateSerializer /
// BusinessUpdateSerializer), so API field errors map straight onto inputs.
const ALL_FIELDS = [...IDENTIFICATION_FIELDS, ...COMPANY_FIELDS, ...ADDRESS_ROW_FIELDS, 'persona_parent_company_uuid'];

const FIELD_LABEL_KEY = {
  customer_uuid: 'customer',
  display_name: 'displayName',
  language: 'language',
  persona_parent_company_uuid: 'parentCompany',
  ...COMPANY_FIELD_LABEL_KEY,
};

const FIELD_HELPER_KEY = {
  customer_uuid: 'customerHelper',
  display_name: 'displayNameHelper',
  language: 'languageHelper',
  persona_parent_company_uuid: 'parentCompanyHelper',
  ...COMPANY_FIELD_HELPER_KEY,
};

// Applied on every keystroke/paste, so the field never holds anything else.
const FIELD_NORMALIZERS = {
  persona_tax_id: normalizeTaxId,
  persona_country: normalizeCountry,
  address_country: normalizeCountry,
};

// Max length / type of each input, matching the Django models (Business +
// the company ones shared with the customer form).
const FIELD_RULES = {
  display_name: { maxLength: 255 },
  ...COMPANY_FIELD_RULES,
};
const PHONE_NUMBER_RULES = COMPANY_FIELD_RULES.phone_number;

// Input masks by the data's country (the value in state stays raw).
const FIELD_MASKS = {
  persona_tax_id: (values) => TAX_ID_MASKS[values.persona_country],
  // The address country is the company's (see handleCompanyCountryChange).
  address_postal_code: (values) => POSTAL_CODE_MASKS[values.persona_country],
};

const emptyValues = () => ({
  ...Object.fromEntries(ALL_FIELDS.map((field) => [field, ''])),
  persona_country: 'BR',
  address_country: 'BR',
});

const primaryAddress = (addresses = []) =>
  addresses.find((address) => address.address_type === 'COMMERCIAL' && address.is_primary) ?? null;

// Phones are edited formatted ("+55 11 99876-5432") and sent as E.164.
const phonesFrom = (phones = []) =>
  phones.length
    ? phones.map((phone) => ({
        uuid: phone.uuid,
        number: formatPhoneInput(phone.number ?? ''),
        type: phone.type || 'landline',
      }))
    : [emptyPhone()];

// DRF errors for `phones`: a list-level message ("may not be empty") or one
// entry per phone (`{number: [...]}` / `{}` for the valid ones).
const parsePhoneErrors = (error) => {
  const value = error?.response?.data?.phones;
  if (!value) return { list: '', items: [] };
  if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
    return { list: value.join(' '), items: [] };
  }
  if (!Array.isArray(value)) {
    return { list: Object.values(value).flat().join(' '), items: [] };
  }
  return {
    list: '',
    items: value.map((item) => (item && typeof item === 'object' ? Object.values(item).flat().join(' ') : '')),
  };
};

// Company + address values from a persona and its addresses (a loaded
// business on edit, or the persona a reused tax ID points to on create).
const companyValues = (persona = {}, addresses = []) => {
  const address = primaryAddress(addresses);
  return {
    persona_full_name: persona.full_name ?? '',
    persona_legal_name: persona.legal_name ?? '',
    // Older records may still carry punctuation; show (and save) them clean.
    persona_tax_id: normalizeTaxId(persona.tax_id ?? ''),
    persona_registration_number: persona.registration_number ?? '',
    persona_email: persona.email ?? '',
    persona_website: persona.website ?? '',
    persona_country: persona.country ?? '',
    address_street: address?.street ?? '',
    address_number: address?.number ?? '',
    address_complement: address?.complement ?? '',
    address_district: address?.district ?? '',
    address_city: address?.city ?? '',
    address_state: address?.state ?? '',
    address_postal_code: address?.postal_code ?? '',
    address_country: address?.country ?? persona.country ?? 'BR',
  };
};

const valuesFromBusiness = (business) => ({
  ...emptyValues(),
  customer_uuid: business.customer_uuid ?? '',
  display_name: business.display_name ?? '',
  language: business.language ?? '',
  ...companyValues(business.persona, business.addresses),
});

export default function BusinessForm() {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { collaborator } = useAuth();
  // Creating a store depends on the customer's contract: internal users
  // only (IsInternalUser on the backend). Edit is per record (can_edit).
  const isInternal = Boolean(collaborator?.is_internal);

  const businessQuery = useQuery({
    queryKey: ['businesses', id],
    retry: retryOnServerError,
    queryFn: () => customerService.getBusiness(id).then((res) => res.data),
    enabled: isEditMode,
  });
  const business = businessQuery.data;

  const customersQuery = useQuery({
    queryKey: ['customers'],
    retry: retryOnServerError,
    queryFn: () => customerService.getCustomers().then((res) => res.data),
    enabled: !isEditMode && isInternal,
  });
  const customers = useMemo(
    () => (customersQuery.data ?? []).filter((customer) => !customer.is_deleted),
    [customersQuery.data],
  );

  const [values, setValues] = useState(() => ({
    ...emptyValues(),
    customer_uuid: (!isEditMode && searchParams.get(CUSTOMER_PARAM)) || '',
  }));
  const [phones, setPhones] = useState(() => [emptyPhone()]);
  const [phoneErrors, setPhoneErrors] = useState({ list: '', items: [] });
  const [isActive, setIsActive] = useState(true);
  const [companyType, setCompanyType] = useState('independent');
  // Create only: the existing persona this tax ID points to (same company),
  // reused as is by the backend — company/address fields are then locked.
  const [reusedPersona, setReusedPersona] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [touched, setTouched] = useState(false);
  // Fields (and `phone-<index>` rows) the user already left.
  const [blurred, setBlurred] = useState({});
  const [flashMessage, setFlashMessage] = useState('');

  const customerUuid = values.customer_uuid;

  // State/city option lists (by address country) and postal code autofill.
  const locations = useAddressLocations({ values, setValues, countryField: 'persona_country' });
  const numberInputRef = useRef(null);
  const postalCode = usePostalCodeLookup({
    // A new postal code replaces street/district/city/state; number and
    // complement are kept.
    onFound: (address) => {
      setValues((prev) => ({
        ...prev,
        address_street: address.street,
        address_district: address.district,
        address_city: address.city,
        address_state: address.state,
      }));
      setFieldErrors((prev) => ({
        ...prev,
        address_street: undefined,
        address_district: undefined,
        address_city: undefined,
        address_state: undefined,
      }));
      numberInputRef.current?.focus();
    },
  });

  const parentsQuery = useQuery({
    queryKey: ['businesses', 'parent-companies', customerUuid],
    retry: retryOnServerError,
    queryFn: () => customerService.getBusinessParentCompanies(customerUuid).then((res) => res.data),
    enabled: !isEditMode && isInternal && companyType === 'branch' && Boolean(customerUuid),
  });
  const parents = parentsQuery.data ?? [];

  useEffect(() => {
    if (isEditMode && business) {
      setValues(valuesFromBusiness(business));
      setPhones(phonesFrom(business.phones));
      setIsActive(business.is_active);
    }
  }, [isEditMode, business]);

  const isReusing = !isEditMode && Boolean(reusedPersona);
  const isShared = isEditMode && Boolean(business?.is_shared_persona);
  const hadAddress = isEditMode && Boolean(primaryAddress(business?.addresses));
  // On edit the address is only sent (and then required) once it exists or
  // the user starts filling it in.
  const sendsAddress =
    !isEditMode ||
    hadAddress ||
    ADDRESS_ROW_FIELDS.some((field) => field !== 'address_country' && values[field].trim());

  const filledPhones = phones
    // On edit, the uuid keeps an existing phone instead of replacing it.
    .map((phone) => ({
      ...(isEditMode && phone.uuid ? { uuid: phone.uuid } : {}),
      number: toE164(phone.number),
      type: phone.type,
    }))
    .filter((phone) => phone.number);
  // At least one contact phone for a new company; on edit only once it has
  // any (the backend can't blank the list out).
  const hadPhones = isEditMode && (business?.phones ?? []).length > 0;
  const phonesRequired = (!isEditMode && !isReusing) || hadPhones;
  const phonesMissing = phonesRequired && filledPhones.length === 0;

  const requiredFields = (() => {
    if (isEditMode) {
      return [
        'display_name',
        ...REQUIRED_COMPANY_FIELDS,
        ...(sendsAddress ? REQUIRED_NEW_ADDRESS_FIELDS : []),
      ];
    }
    if (isReusing) return ['customer_uuid', 'display_name', 'persona_tax_id'];
    return [
      'customer_uuid',
      'display_name',
      ...REQUIRED_COMPANY_FIELDS,
      ...REQUIRED_ADDRESS_FIELDS.filter((field) => field !== 'phone_number'),
      ...(companyType === 'branch' ? ['persona_parent_company_uuid'] : []),
    ];
  })();
  const isLocked = (field) =>
    (isReusing && [...COMPANY_FIELDS, ...ADDRESS_ROW_FIELDS].includes(field) && field !== 'persona_tax_id') ||
    (isShared && SHARED_LOCKED_FIELDS.includes(field));

  // Locked fields aren't sent (reused/shared company data), so they never block.
  const fieldIssue = (field) =>
    isLocked(field)
      ? null
      : validateFieldValue(values[field], { required: requiredFields.includes(field), ...FIELD_RULES[field] }) ||
        locations.issue(field);
  const phoneIssue = (phone) => validateFieldValue(phone.number, PHONE_NUMBER_RULES);
  // address_country isn't a field of its own: it mirrors persona_country.
  const isValid =
    ALL_FIELDS.filter((field) => field !== 'address_country').every((field) => !fieldIssue(field)) &&
    !phonesMissing && phones.every((phone) => !phoneIssue(phone));
  const issueText = (issue) => t(`businesses.validation.${issue.key}`, issue.params);

  const setField = (field) => (event) => {
    const normalize = FIELD_NORMALIZERS[field];
    const value = normalize ? normalize(event.target.value) : event.target.value;
    setValues((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const setFieldValue = (field, value) => setField(field)({ target: { value } });

  // The address uses the company's country (no separate address country
  // for now — covers most cases). Cascade: another country means other
  // states/cities (and postal code format); another state, other cities.
  const handleCompanyCountryChange = (country) => {
    setValues((prev) => ({
      ...prev,
      persona_country: country,
      address_country: country,
      address_state: '',
      address_city: '',
    }));
    setFieldErrors((prev) => ({ ...prev, persona_country: undefined, address_country: undefined }));
    postalCode.reset();
  };

  const handleAddressStateChange = (state) => {
    setValues((prev) => ({ ...prev, address_state: state, address_city: '' }));
    setFieldErrors((prev) => ({ ...prev, address_state: undefined }));
  };

  const handlePostalCodeChange = (event) => {
    setField('address_postal_code')(event);
    postalCode.lookup(values.persona_country, event.target.value);
  };

  // Another tax ID or customer means another (maybe new) company: drop the
  // reused persona and give the fields back (blank, not the reused data).
  const releaseReusedPersona = () => {
    if (!reusedPersona) return;
    setReusedPersona(null);
    setValues((prev) => ({
      ...prev,
      ...companyValues({ country: 'BR' }),
      persona_tax_id: prev.persona_tax_id,
    }));
    setPhones([emptyPhone()]);
  };

  const lookupPersona = async (customer, taxId) => {
    if (isEditMode || !customer || !taxId) return;
    try {
      const { data } = await customerService.lookupBusinessPersona({ customer, taxId });
      if (!data.persona) return;
      setReusedPersona(data);
      setValues((prev) => ({
        ...prev,
        ...companyValues(data.persona, data.addresses),
        persona_tax_id: prev.persona_tax_id,
      }));
      setPhones(phonesFrom(data.phones));
      setFieldErrors({});
      setPhoneErrors({ list: '', items: [] });
    } catch {
      // Best effort: without the lookup the backend still reuses the persona.
    }
  };

  const handleTaxIdChange = (event) => {
    releaseReusedPersona();
    setField('persona_tax_id')(event);
  };

  const handleCustomerChange = (event) => {
    releaseReusedPersona();
    setField('customer_uuid')(event);
    setValues((prev) => ({ ...prev, persona_parent_company_uuid: '' }));
    lookupPersona(event.target.value, values.persona_tax_id);
  };

  const markBlurred = (key) => setBlurred((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  // Client-side problems show once the user left the field or tried to save.
  const shownIssue = (key, issue) => (touched || blurred[key]) && issue;

  const fieldProps = (field) => {
    const issue = shownIssue(field, fieldIssue(field));
    const helperKey = FIELD_HELPER_KEY[field];
    const { maxLength } = FIELD_RULES[field] ?? {};
    return {
      label: t(`businesses.fields.${FIELD_LABEL_KEY[field]}`),
      value: values[field],
      onChange: setField(field),
      onBlur: () => markBlurred(field),
      required: requiredFields.includes(field),
      disabled: isLocked(field),
      error: Boolean(issue || fieldErrors[field]),
      // Always some helper text (' ' when there's no hint): the line is
      // reserved, so an error showing up doesn't push the layout (and the
      // save button the user is clicking) down.
      helperText: issue
        ? issueText(issue)
        : fieldErrors[field] || (helperKey ? t(`businesses.fields.${helperKey}`) : ' '),
      slotProps: maskedInputSlotProps(FIELD_MASKS[field]?.(values), maxLength ? { maxLength } : undefined),
      fullWidth: true,
    };
  };

  const updatePhone = (index, changes) => {
    setPhones((prev) => prev.map((phone, i) => (i === index ? { ...phone, ...changes } : phone)));
    setPhoneErrors((prev) => ({ list: '', items: prev.items.map((message, i) => (i === index ? '' : message)) }));
  };

  const addPhone = () => setPhones((prev) => [...prev, emptyPhone()]);

  const removePhone = (index) => {
    setPhones((prev) => prev.filter((_, i) => i !== index));
    setPhoneErrors((prev) => ({ list: prev.list, items: prev.items.filter((_, i) => i !== index) }));
  };

  const resetFormFields = () => {
    setValues((prev) => ({ ...emptyValues(), customer_uuid: prev.customer_uuid }));
    setPhones([emptyPhone()]);
    setCompanyType('independent');
    setReusedPersona(null);
    setTouched(false);
    setBlurred({});
  };

  const trimmed = (fields) => Object.fromEntries(fields.map((field) => [field, values[field].trim()]));

  const buildPayload = () => {
    if (isEditMode) {
      const companyFields = isShared
        ? COMPANY_FIELDS.filter((field) => !SHARED_LOCKED_FIELDS.includes(field))
        : COMPANY_FIELDS;
      const payload = {
        ...trimmed(['display_name', 'language', ...companyFields]),
        ...(sendsAddress ? { ...trimmed(ADDRESS_ROW_FIELDS), address_country: values.persona_country } : {}),
        is_active: isActive,
      };
      // `phones` replaces the whole list; it can't be blanked out.
      if (filledPhones.length) payload.phones = filledPhones;
      return payload;
    }
    if (isReusing) return trimmed(['customer_uuid', 'display_name', 'language', 'persona_tax_id']);
    const payload = {
      ...trimmed([...IDENTIFICATION_FIELDS, ...COMPANY_FIELDS, ...ADDRESS_ROW_FIELDS]),
      address_country: values.persona_country,
      phones: filledPhones,
      persona_is_headquarters: companyType === 'headquarters',
    };
    if (companyType === 'branch') payload.persona_parent_company_uuid = values.persona_parent_company_uuid;
    return payload;
  };

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['businesses'] }),
      // Customer pages show the stores list/count.
      queryClient.invalidateQueries({ queryKey: ['customers'] }),
    ]);

  const mutation = useMutation({
    mutationFn: () =>
      isEditMode ? customerService.patchBusiness(id, buildPayload()) : customerService.createBusiness(buildPayload()),
    // `intent` is whichever submit button was clicked — 'another' only
    // exists in create mode (see the extra button in CardActions).
    onSuccess: (response, intent) => {
      invalidate();
      if (isEditMode) {
        navigate(`/businesses/${response.data.uuid}`);
        return;
      }
      if (intent === 'another') {
        resetFormFields();
        setFlashMessage(t('businesses.saveSuccessCreate'));
        return;
      }
      navigate('/businesses', { state: { flashMessage: t('businesses.saveSuccessCreate') } });
    },
    onError: (error) => {
      const { fieldErrors: apiFieldErrors, formError: apiFormError } = parseApiFormErrors(error, [
        ...ALL_FIELDS,
        'phones',
      ]);
      setFieldErrors(apiFieldErrors);
      setPhoneErrors(parsePhoneErrors(error));
      if (error?.response?.status === 403) {
        setFormError(t('businesses.states.permissionDeniedMessage'));
        return;
      }
      setFormError(apiFormError || (Object.keys(apiFieldErrors).length ? '' : t('businesses.saveError')));
    },
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    setTouched(true);
    setFieldErrors({});
    setPhoneErrors({ list: '', items: [] });
    setFormError('');
    if (!isValid) return;
    const intent = event.nativeEvent.submitter?.value || 'list';
    mutation.mutate(intent);
  };

  const header = (
    <PageHeader
      title={isEditMode ? t('businesses.editTitle') : t('businesses.createTitle')}
      breadcrumbs={[
        { label: t('businesses.breadcrumb.dashboard') },
        { label: t('nav.groups.settings') },
        { label: t('businesses.breadcrumb.list'), to: '/businesses' },
        { label: isEditMode ? t('businesses.breadcrumb.edit') : t('businesses.breadcrumb.new') },
      ]}
    />
  );

  const permissionDenied = (
    <BoundedPageLayout header={header}>
      <PermissionDenied
        title={t('businesses.states.permissionDeniedTitle')}
        message={t('businesses.states.permissionDeniedMessage')}
      />
    </BoundedPageLayout>
  );

  if (!isEditMode && !isInternal) return permissionDenied;

  // On create, wait for the customers too: a ?customer= preselection would
  // otherwise hit the Select before its options exist (MUI out-of-range).
  if ((isEditMode && businessQuery.isLoading) || (!isEditMode && customersQuery.isLoading)) {
    return (
      <BoundedPageLayout header={header}>
        <LoadingState message={t('businesses.states.loading')} />
      </BoundedPageLayout>
    );
  }

  if (isEditMode && businessQuery.isError) {
    const httpStatus = businessQuery.error?.response?.status;
    if (httpStatus === 403) return permissionDenied;
    return (
      <BoundedPageLayout header={header}>
        {httpStatus === 404 ? (
          <ErrorState title={t('businesses.states.notFoundTitle')} message={t('businesses.states.notFoundMessage')} />
        ) : (
          <ErrorState
            title={t('businesses.states.errorTitle')}
            message={t('businesses.states.errorMessage')}
            actionLabel={t('businesses.states.retry')}
            onRetry={businessQuery.refetch}
          />
        )}
      </BoundedPageLayout>
    );
  }

  if (isEditMode && !businessCapabilities(business).canEdit) return permissionDenied;

  const languageNoneLabel = isEditMode ? t('businesses.fields.languageNone') : t('businesses.fields.languageInherit');
  const reusedType = reusedPersona?.persona?.company_type;

  return (
    <BoundedPageLayout header={header}>
      <Stack component="form" onSubmit={handleSubmit} noValidate spacing={3} sx={{ width: '100%', pb: 3 }}>
        <Card>
          <CardHeader
            title={t('businesses.sections.identification')}
            subheader={t('businesses.sections.identificationHint')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent>
            <Stack spacing={2.5}>
              {isEditMode ? (
                <Stack>
                  <Typography variant="body2" color="text.secondary">
                    {t('businesses.fields.customer')}
                  </Typography>
                  <Typography variant="body1">{business.customer_name}</Typography>
                </Stack>
              ) : (
                <TextField select {...fieldProps('customer_uuid')} onChange={handleCustomerChange}>
                  {customers.map((option) => (
                    <MenuItem key={option.uuid} value={option.uuid}>
                      {option.display_name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField {...fieldProps('display_name')} />
                <TextField
                  select
                  {...fieldProps('language')}
                  // '' is a real choice here (inherit on create, none on
                  // edit), so show it instead of an empty-looking field.
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (value) => {
                      const language = languages.find((option) => option.code === value);
                      return language ? <FlagOption language={language} /> : languageNoneLabel;
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">{languageNoneLabel}</MenuItem>
                  {languages.map((language) => (
                    <MenuItem key={language.code} value={language.code}>
                      <FlagOption language={language} />
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              {isEditMode && (
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack>
                    <Typography variant="body1">{t('businesses.fields.isActive')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('businesses.fields.isActiveHelper')}
                    </Typography>
                  </Stack>
                  <Switch checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title={t('businesses.sections.hierarchy')}
            subheader={t('businesses.sections.hierarchyHint')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent>
            {isEditMode || isReusing ? (
              // Set on creation only; a reused persona keeps its own type.
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <CompanyTypeChip t={t} companyType={isEditMode ? business.persona?.company_type : reusedType} />
                {isEditMode && business.parent_company && (
                  <Typography variant="body2" color="text.secondary">
                    {t('businesses.fields.parentCompany')}: {business.parent_company.full_name}
                  </Typography>
                )}
              </Stack>
            ) : (
              <Stack spacing={2.5}>
                <TextField
                  select
                  label={t('businesses.fields.companyType')}
                  value={companyType}
                  onChange={(event) => {
                    setCompanyType(event.target.value);
                    setFieldErrors((prev) => ({ ...prev, persona_parent_company_uuid: undefined }));
                  }}
                  helperText={t(`businesses.companyTypeHelpers.${companyType}`)}
                  fullWidth
                >
                  {COMPANY_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {t(`businesses.companyTypes.${type}`)}
                    </MenuItem>
                  ))}
                </TextField>
                {companyType === 'branch' && (
                  <TextField
                    select
                    {...fieldProps('persona_parent_company_uuid')}
                    disabled={!customerUuid || parentsQuery.isLoading}
                    helperText={
                      (shownIssue('persona_parent_company_uuid', fieldIssue('persona_parent_company_uuid')) &&
                        t('businesses.validation.required')) ||
                      fieldErrors.persona_parent_company_uuid ||
                      (customerUuid && parentsQuery.isSuccess && parents.length === 0
                        ? t('businesses.noParents')
                        : t('businesses.fields.parentCompanyHelper'))
                    }
                  >
                    {parents.map((parent) => (
                      <MenuItem key={parent.uuid} value={parent.uuid}>
                        <Stack>
                          <Typography variant="body2">{parent.full_name}</Typography>
                          {(parent.business_names.length > 0 || parent.tax_id) && (
                            <Typography variant="caption" color="text.secondary">
                              {[parent.tax_id, ...parent.business_names].filter(Boolean).join(' · ')}
                            </Typography>
                          )}
                        </Stack>
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title={t('businesses.sections.company')}
            subheader={t('businesses.sections.companyHint')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent>
            <Stack spacing={2.5}>
              {isReusing && (
                <Alert severity="info">
                  {t('businesses.personaReuseNotice', { names: sharedNames(t, reusedPersona.shared_with) })}
                </Alert>
              )}
              {isShared && (
                <Alert severity="warning">
                  {t('businesses.sharedNotice', { names: sharedNames(t, business.shared_with) })}
                </Alert>
              )}
              {/* Country first: it drives the tax ID mask and the address
                  (postal code, states and cities). */}
              <Box sx={{ maxWidth: { sm: 'calc(50% - 8px)' } }}>
                <CountryAutocomplete
                  value={values.persona_country}
                  onChange={handleCompanyCountryChange}
                  textFieldProps={fieldProps('persona_country')}
                />
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                {/* Tax ID first: on create it decides whether the company
                    already exists for this customer (and is reused). */}
                <TextField
                  {...fieldProps('persona_tax_id')}
                  onChange={handleTaxIdChange}
                  onBlur={() => {
                    markBlurred('persona_tax_id');
                    lookupPersona(customerUuid, values.persona_tax_id);
                  }}
                />
                <TextField {...fieldProps('persona_registration_number')} />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField {...fieldProps('persona_full_name')} />
                <TextField {...fieldProps('persona_legal_name')} />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField type="email" {...fieldProps('persona_email')} />
                <TextField type="url" {...fieldProps('persona_website')} />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title={t('businesses.sections.address')}
            subheader={t('businesses.sections.addressHint')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent>
            <Stack spacing={2.5}>
              {/* Postal code first: it fills the rest (country = the company's). */}
              <Box sx={{ maxWidth: { sm: 'calc(50% - 8px)' } }}>
                <PostalCodeField
                  status={postalCode.status}
                  fieldProps={{ ...fieldProps('address_postal_code'), onChange: handlePostalCodeChange }}
                />
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField {...fieldProps('address_street')} />
                <TextField
                  {...fieldProps('address_number')}
                  inputRef={numberInputRef}
                  sx={{ maxWidth: { sm: 160 } }}
                />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField {...fieldProps('address_complement')} />
                <TextField {...fieldProps('address_district')} />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <StateAutocomplete
                    locations={locations.states}
                    value={values.address_state}
                    onChange={handleAddressStateChange}
                    textFieldProps={fieldProps('address_state')}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <CityAutocomplete
                    locations={locations.cities}
                    state={values.address_state}
                    value={values.address_city}
                    onChange={(city) => setFieldValue('address_city', city)}
                    textFieldProps={fieldProps('address_city')}
                  />
                </Box>
              </Stack>
              <Divider />
              <Stack spacing={2}>
                <Stack>
                  <Typography variant="subtitle2">{t('businesses.fields.phones')}</Typography>
                  <Typography
                    variant="body2"
                    color={(touched && phonesMissing) || phoneErrors.list ? 'error' : 'text.secondary'}
                  >
                    {(touched && phonesMissing && t('businesses.validation.phoneRequired')) ||
                      phoneErrors.list ||
                      t('businesses.fields.phonesHelper')}
                  </Typography>
                </Stack>
                {phones.map((phone, index) => (
                  <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
                    <TextField
                      select
                      label={t('businesses.fields.phoneType')}
                      value={phone.type}
                      onChange={(event) => updatePhone(index, { type: event.target.value })}
                      disabled={isReusing}
                      sx={{ minWidth: { sm: 180 } }}
                    >
                      {PHONE_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {t(`businesses.phoneTypes.${type}`)}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      type="tel"
                      label={t('businesses.fields.phone')}
                      value={phone.number}
                      onChange={(event) => updatePhone(index, { number: nextPhoneInput(phone.number, event.target.value) })}
                      onBlur={() => markBlurred(`phone-${index}`)}
                      disabled={isReusing}
                      slotProps={{ htmlInput: { maxLength: PHONE_NUMBER_RULES.maxLength } }}
                      error={Boolean(shownIssue(`phone-${index}`, phoneIssue(phone)) || phoneErrors.items[index])}
                      helperText={
                        (shownIssue(`phone-${index}`, phoneIssue(phone)) && issueText(phoneIssue(phone))) ||
                        phoneErrors.items[index] ||
                        t('businesses.fields.phoneHelper')
                      }
                      fullWidth
                    />
                    <Tooltip title={t('businesses.actions.removePhone')}>
                      {/* span: a disabled button can't fire the tooltip's events */}
                      <span>
                        <IconButton
                          aria-label={t('businesses.actions.removePhone')}
                          onClick={() => removePhone(index)}
                          disabled={isReusing || phones.length === 1}
                          sx={{ mt: { sm: 1 } }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                ))}
                {!isReusing && (
                  <Button startIcon={<AddIcon />} onClick={addPhone} sx={{ alignSelf: 'flex-start' }}>
                    {t('businesses.actions.addPhone')}
                  </Button>
                )}
              </Stack>
              {formError && <Alert severity="error">{formError}</Alert>}
            </Stack>
          </CardContent>
          <Divider />
          <CardActions sx={{ px: 3, py: 2, gap: 1, justifyContent: 'flex-end' }}>
            {/* Back to wherever the user came from (list, business or
                customer detail) — "Cancel" means undo getting here. */}
            <Button type="button" color="inherit" onClick={() => navigate(-1)}>
              {t('businesses.actions.cancel')}
            </Button>
            {!isEditMode && (
              <Button type="submit" name="intent" value="another" variant="outlined" disabled={mutation.isPending}>
                {t('businesses.actions.saveAndAddAnother')}
              </Button>
            )}
            <Button type="submit" name="intent" value="list" variant="contained" disabled={mutation.isPending}>
              {isEditMode ? t('businesses.actions.saveEdit') : t('businesses.actions.saveCreate')}
            </Button>
          </CardActions>
        </Card>
      </Stack>

      <SuccessSnackbar open={Boolean(flashMessage)} message={flashMessage} onClose={() => setFlashMessage('')} />
    </BoundedPageLayout>
  );
}
